/**
 * @fileoverview V2 Basics Step — Firm, client name, case type, task gating.
 *
 * Self-contained: owns its own form state, validation, and submit handler.
 * On submit it dispatches MATTER_CREATED and navigates to the next step.
 * When resuming an existing matter it hydrates from API and allows editing.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { skipToken } from '@reduxjs/toolkit/query';
import { Globe, Loader2 } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';

import { DuplicateSuggestions } from '@/components/matter-form/DuplicateSuggestions';
import { useDuplicateDetection } from '@/components/matter-form/useDuplicateDetection';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Combobox,
  ComboboxLabel,
  ComboboxOption,
} from '@/components/ui/catalyst/combobox';
import {
  useCreateCaseIngestionMutation,
  useStartCaseIngestionWorkflowMutation,
} from '@/services/api/caseIngestionService';
import { useGetFirmsQuery } from '@/services/api/firmsService';
import {
  useCreateMatterMutation,
  useGetMatterDetailsQuery,
  usePatchMatterClientMutation,
  usePatchMatterMutation,
} from '@/services/api/mattersService';
import { MatterStatus, MatterType } from '@/services/types/client-intake-types';
import { selectUser } from '@/state/selectors/root-selectors';
import {
  getCmsTargetSystemFromUrl,
  hasCmsAutoSync,
} from '@/utils/externalSystemUtils';

import {
  FormCheckbox,
  FormError,
  FormField,
  FormGroup,
  FormInput,
  FormLabel,
  FormSection,
  FormSelect,
} from '../components/form';
import { useIngestion } from '../state/IngestionProvider';

const basicsSchema = z.object({
  firm: z.object({
    id: z.string().min(1, 'Firm is required'),
    name: z.string().min(1),
  }),
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  matter_type: z.nativeEnum(MatterType, {
    errorMap: () => ({ message: 'Case type is required' }),
  }),
  gate_tasks_during_initial_review: z.boolean().default(false),
  external_cms_url: z.string().optional(),
});

type BasicsFormValues = z.infer<typeof basicsSchema>;

type FirmOption = {
  id: string;
  name: string;
};

type CreatedMatterResult = {
  matterId: string;
  matterName: string;
};

function formatMatterTypeLabel(matterType: MatterType): string {
  return matterType
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

const CASE_TYPE_OPTIONS = Object.values(MatterType)
  .filter((matterType) => matterType !== MatterType.UNKNOWN)
  .map((matterType) => ({
    value: matterType,
    label: formatMatterTypeLabel(matterType),
  }));

export const BasicsStep = (): React.JSX.Element => {
  const { state, dispatch } = useIngestion();
  const navigate = useNavigate();
  const user = useSelector(selectUser);

  const { data: firms, isLoading: firmsLoading } = useGetFirmsQuery({});
  const { data: existingMatter } = useGetMatterDetailsQuery(
    state.matterId ?? skipToken,
  );

  const {
    register,
    control,
    handleSubmit,
    setValue,
    reset,
    watch,
    trigger,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<BasicsFormValues>({
    resolver: zodResolver(basicsSchema),
    defaultValues: {
      firm: { id: '', name: '' },
      first_name: '',
      last_name: '',
      matter_type: undefined,
      gate_tasks_during_initial_review: false,
      external_cms_url: '',
    },
  });

  const [createMatter] = useCreateMatterMutation();
  const [patchClient] = usePatchMatterClientMutation();
  const [patchMatter] = usePatchMatterMutation();
  const [createCaseIngestion] = useCreateCaseIngestionMutation();
  const [startWorkflow] = useStartCaseIngestionWorkflowMutation();
  const [isStartingIngestion, setIsStartingIngestion] = useState(false);

  // Duplicate detection
  const firstName = watch('first_name') || '';
  const lastName = watch('last_name') || '';
  const fullName = `${firstName} ${lastName}`.trim();
  const { duplicates } = useDuplicateDetection(fullName);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [pendingCreation, setPendingCreation] = useState(false);

  const cmsUrl = watch('external_cms_url');
  const detectedCms = useMemo(() => getCmsTargetSystemFromUrl(cmsUrl), [cmsUrl]);
  const canAutoIngest = useMemo(() => hasCmsAutoSync(detectedCms), [detectedCms]);
  const hasHydrated = useRef(false);

  useEffect(() => {
    if (!existingMatter || hasHydrated.current) {
      return;
    }

    hasHydrated.current = true;

    const primaryClient = existingMatter.clients?.[0];

    reset({
      firm: existingMatter.firm
        ? { id: existingMatter.firm.id, name: existingMatter.firm.name }
        : { id: '', name: '' },
      first_name: primaryClient?.first_name ?? '',
      last_name: primaryClient?.last_name ?? '',
      matter_type: existingMatter.matter_type ?? undefined,
      gate_tasks_during_initial_review:
        existingMatter.task_config?.gate_tasks_during_initial_review ?? false,
      external_cms_url: existingMatter.external_cms_url ?? '',
    });
  }, [existingMatter, reset]);

  const firmOptions = useMemo<FirmOption[]>(
    () => firms?.map((firm) => ({ id: firm.id, name: firm.name })) ?? [],
    [firms],
  );

  const matterExists = !!state.matterId;
  const isLoading = isSubmitting || firmsLoading;
  const isBusy = isLoading || isStartingIngestion;

  async function createMatterRecord(
    data: BasicsFormValues,
  ): Promise<CreatedMatterResult> {
    if (!user) {
      throw new Error('User is required to create a matter');
    }

    const result = await createMatter({
      firm: data.firm.id,
      creator: user.id,
      name: `${data.first_name} ${data.last_name}`.trim(),
      status: MatterStatus.CASE_INGESTION,
      matter_type: data.matter_type,
      clients: [
        {
          first_name: data.first_name,
          last_name: data.last_name,
          phone_numbers: [],
        },
      ],
      task_config: {
        gate_tasks_during_initial_review:
          data.gate_tasks_during_initial_review ?? false,
      },
    }).unwrap();

    const matterId = result.matter.id;
    const primaryClientId = result.matter.clients?.[0]?.id;
    const externalCmsUrl = data.external_cms_url?.trim();

    if (primaryClientId) {
      await patchClient({
        matter_id: matterId,
        matter_client_id: primaryClientId,
        patch: {
          first_name: data.first_name,
          last_name: data.last_name,
        },
      }).unwrap();
    }

    if (externalCmsUrl) {
      await patchMatter({
        matter_id: matterId,
        patch: { external_cms_url: externalCmsUrl },
      }).unwrap();
    }

    await createCaseIngestion({ matterId }).unwrap();

    return {
      matterId,
      matterName: result.matter.name,
    };
  }

  function navigateToMatterIngestion(matterId: string): void {
    dispatch({ type: 'MATTER_CREATED', matterId });
    navigate(`/matters/${matterId}/ingestion/v2`, { replace: true });
  }

  async function handleCreateAndStartIngestion(): Promise<void> {
    const isValid = await trigger();
    if (!isValid) {
      return;
    }

    const formValues = getValues();
    const externalCmsUrl = formValues.external_cms_url?.trim();

    if (!externalCmsUrl) {
      toast.error('Enter a CMS URL to start ingestion');
      return;
    }

    if (!user) {
      toast.error('You must be logged in');
      return;
    }

    // Check for duplicates before creating
    if (duplicates.length > 0 && !pendingCreation) {
      setShowDuplicateDialog(true);
      return;
    }

    setIsStartingIngestion(true);

    try {
      const { matterId } = await createMatterRecord({
        ...formValues,
        external_cms_url: externalCmsUrl,
      });

      await startWorkflow({
        matterId,
        data: { external_cms_url: externalCmsUrl },
      }).unwrap();

      toast.success('Matter created & CMS ingestion started');
      navigateToMatterIngestion(matterId);
    } catch {
      toast.error('Failed to create matter or start ingestion');
    } finally {
      setIsStartingIngestion(false);
    }
  }

  async function onSubmit(data: BasicsFormValues): Promise<void> {
    if (matterExists) {
      dispatch({ type: 'GO_TO_STEP', index: state.stepIndex + 1 });
      return;
    }

    if (!user) {
      toast.error('You must be logged in to create a matter');
      return;
    }

    // Check for duplicates before creating (soft block with dialog)
    if (duplicates.length > 0 && !pendingCreation) {
      setShowDuplicateDialog(true);
      return;
    }

    try {
      const { matterId, matterName } = await createMatterRecord(data);
      toast.success(`Matter "${matterName}" created successfully`);
      setPendingCreation(false);
      navigateToMatterIngestion(matterId);
    } catch {
      toast.error('Failed to create matter');
      setPendingCreation(false);
    }
  }

  function handleConfirmCreateAnyway(): void {
    setShowDuplicateDialog(false);
    setPendingCreation(true);
    // Re-trigger submit — pendingCreation flag bypasses duplicate check
    handleSubmit(onSubmit)();
  }

  return (
    <>
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
      <FormSection
        title="New Matter"
        description="Enter the basics to create a matter."
      >
        <FormGroup>
          <FormField hasError={!!errors.firm?.id}>
            <FormLabel required>Firm</FormLabel>
            <Controller
              name="firm"
              control={control}
              render={({ field }) => (
                <Combobox
                  value={field.value?.id ? field.value : undefined}
                  onChange={(firm: FirmOption | null) => {
                    setValue('firm', firm ?? { id: '', name: '' }, {
                      shouldValidate: true,
                    });
                  }}
                  options={firmOptions}
                  displayValue={(firm: FirmOption | null) => firm?.name ?? ''}
                  placeholder="Select a firm"
                  className="w-full"
                  aria-label="Select a firm"
                  disabled={firmsLoading || isLoading || matterExists}
                >
                  {(option: FirmOption) => (
                    <ComboboxOption value={option}>
                      <ComboboxLabel>{option.name}</ComboboxLabel>
                    </ComboboxOption>
                  )}
                </Combobox>
              )}
            />
            <FormError>{errors.firm?.id?.message}</FormError>
          </FormField>

          <div className="grid grid-cols-2 gap-4">
            <FormField hasError={!!errors.first_name}>
              <FormLabel htmlFor="v2-first-name" required>
                First Name
              </FormLabel>
              <FormInput
                id="v2-first-name"
                disabled={isLoading}
                invalid={!!errors.first_name}
                {...register('first_name')}
              />
              <FormError>{errors.first_name?.message}</FormError>
            </FormField>

            <FormField hasError={!!errors.last_name}>
              <FormLabel htmlFor="v2-last-name" required>
                Last Name
              </FormLabel>
              <FormInput
                id="v2-last-name"
                disabled={isLoading}
                invalid={!!errors.last_name}
                {...register('last_name')}
              />
              <FormError>{errors.last_name?.message}</FormError>
            </FormField>
          </div>

          <FormField hasError={!!errors.matter_type}>
            <FormLabel required>Case Type</FormLabel>
            <Controller
              name="matter_type"
              control={control}
              render={({ field }) => (
                <FormSelect
                  disabled={isLoading}
                  invalid={!!errors.matter_type}
                  value={field.value ?? ''}
                  onChange={(event) => {
                    field.onChange(event.target.value || undefined);
                  }}
                  placeholder="Select case type"
                >
                  {CASE_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </FormSelect>
              )}
            />
            <FormError>{errors.matter_type?.message}</FormError>
          </FormField>

          <Controller
            name="gate_tasks_during_initial_review"
            control={control}
            render={({ field }) => (
              <FormCheckbox
                label="Hide tasks during initial review"
                description="Hides all tasks except Retainer, HIPAA, and Initial Review until initial review is complete"
                checked={field.value}
                onChange={field.onChange}
                disabled={isLoading}
              />
            )}
          />
        </FormGroup>
      </FormSection>

      <FormSection
        title="CMS Integration"
        description="Optionally link to an external CMS to auto-populate fields."
      >
        <FormGroup>
          <FormField>
            <FormLabel htmlFor="v2-cms-url">CMS URL</FormLabel>
            <FormInput
              id="v2-cms-url"
              placeholder="https://firm.filevineapp.com/#/project/12345"
              disabled={isBusy}
              {...register('external_cms_url')}
            />
            {detectedCms && (
              <p className="mt-1 text-[11px] font-medium text-emerald-600">
                Detected: {detectedCms}
                {canAutoIngest ? ' (automated ingestion available)' : ''}
              </p>
            )}
          </FormField>
        </FormGroup>
      </FormSection>

      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={isBusy}
          className={
            'inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold ' +
            'bg-blue-600 text-white shadow-sm hover:bg-blue-700 ' +
            'disabled:cursor-not-allowed disabled:opacity-50 ' +
            'transition-colors duration-150'
          }
        >
          {matterExists ? 'Continue' : 'Create Matter'}
        </button>

        {!matterExists && canAutoIngest && (
          <button
            type="button"
            onClick={handleCreateAndStartIngestion}
            disabled={isBusy}
            className={
              'inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold ' +
              'bg-emerald-600 text-white shadow-sm hover:bg-emerald-700 ' +
              'disabled:cursor-not-allowed disabled:opacity-50 ' +
              'transition-colors duration-150'
            }
          >
            {isStartingIngestion ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Globe className="h-3.5 w-3.5" />
            )}
            {isStartingIngestion ? 'Starting...' : 'Create & Start Ingestion'}
          </button>
        )}
      </div>
    </form>

      {/* Duplicate confirmation dialog */}
      <AlertDialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Potential Duplicate Detected</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <p>
                  We found {duplicates.length} similar{' '}
                  {duplicates.length === 1 ? 'matter or intake' : 'matters or intakes'}.
                  Please review before creating a new matter.
                </p>
                <DuplicateSuggestions suggestions={duplicates} />
                <p className="text-sm">Are you sure you want to create a new matter?</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleConfirmCreateAnyway()}>
              Create Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
